/**
 * QuickWhatsAppActions - Fila Welcome · Registre · Codi · Trucar d'una reserva
 * (`plans/dia-a-dia-calendari-i-whatsapp.md` §3.1, §7bis, D3, D5, D6, D9, D13, D14-D16)
 *
 * Al cos del popover del calendari (escriptori, tàctil i pantalla completa del mòbil) i a la
 * fitxa, en lloc del `tel:` de sempre. Al costat, el telèfon en text petit.
 *
 * - Welcome: kind WELCOME. Registre: kind GUEST_FORM, a totes les propietats (D16): l'enllaç del
 *   formulari de viatgers i una frase; el backend reutilitza el formulari actiu o en crea un (D15).
 * - Codi: kind ACCESS, només a les propietats amb missatge de codi
 *   (avui l'hotel, 18770); el codi el calcula el backend a qualsevol hora (D9) i, si no pot,
 *   el flux obre el compositor per escriure'l. Tots dos pel flux compartit
 *   (`useWhatsAppSendFlow`): la tasca queda a la fitxa i es marca «enviat» en tornar (D5).
 * - Trucar: `tel:` amb el telèfon en E.164 (D13: a Android, sense prefix no truca bé).
 * - Sense telèfon en E.164, tots els botons desactivats amb el perquè al títol.
 */

import type { ReactNode } from 'react';
import { ClipboardList, KeyRound, Loader2, MessageCircle, Phone } from 'lucide-react';
import type { MessageKind } from '@guilleumes/types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { resolveQuickWhatsAppState } from '@/lib/messaging';
import { useWhatsAppSendFlow } from './useWhatsAppSendFlow';

interface QuickWhatsAppActionsProps {
  reservationId: string;
  /** El telèfon tal com ve de la reserva: és el que es pinta. */
  phone?: string | null;
  /**
   * El telèfon en E.164 que calcula el backend (`guestPhoneE164`). `null` = el backend diu que
   * no és vàlid; `undefined` = qui pinta no el porta, i es dedueix del brut si té prefix.
   */
  phoneE164?: string | null;
  /** Propietat de Beds24 de la reserva: decideix si surt el botó Codi. */
  beds24PropertyId?: number | null;
  /** Pinta el telèfon al costat dels botons (per defecte, sí). */
  showPhone?: boolean;
  className?: string;
}

/**
 * Classes comunes dels botons: petits, perquè la fila de quatre (tres als pisos) hi càpiga a
 * 385 px i dins dels 320 px del popover d'escriptori (≈ 290 px amb `px-1.5`).
 */
const BUTTON_CLASS = 'h-7 px-1.5 text-xs gap-1';

/**
 * Un botó desactivat no rep el ratolí (`disabled:pointer-events-none` del botó de Shadcn): el
 * títol va a l'embolcall perquè el perquè es vegi igualment.
 */
function TitleWrap({ title, children }: { title: string; children: ReactNode }) {
  return (
    <span className="inline-flex" title={title}>
      {children}
    </span>
  );
}

function FlowButton({
  reservationId,
  messageKind,
  label,
  ariaLabel,
  icon,
  disabledReason,
}: {
  reservationId: string;
  messageKind: MessageKind;
  label: string;
  ariaLabel: string;
  icon: ReactNode;
  disabledReason: string | null;
}) {
  const { start, isCreating, dialogs } = useWhatsAppSendFlow({
    reservationId,
    messageKind,
    title: label,
  });

  return (
    <>
      <TitleWrap title={disabledReason ?? ariaLabel}>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn(BUTTON_CLASS, 'border-green-300 text-green-700 hover:bg-green-50')}
          onClick={start}
          disabled={!!disabledReason || isCreating}
          aria-label={ariaLabel}
        >
          {isCreating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
          {label}
        </Button>
      </TitleWrap>
      {dialogs}
    </>
  );
}

export function QuickWhatsAppActions({
  reservationId,
  phone,
  phoneE164,
  beds24PropertyId,
  showPhone = true,
  className,
}: QuickWhatsAppActionsProps) {
  const { e164, showAccess, disabledReason } = resolveQuickWhatsAppState({
    phone,
    phoneE164,
    beds24PropertyId,
  });

  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-2 gap-y-1', className)}
      data-testid="quick-whatsapp-actions"
    >
      {/* `flex-wrap`: si mai no hi cabés (text més gran), Trucar baixa de línia en lloc de sortir. */}
      <div className="flex flex-wrap items-center gap-1 min-w-0">
        <FlowButton
          reservationId={reservationId}
          messageKind="WELCOME"
          label="Welcome"
          ariaLabel="Enviar el Welcome per WhatsApp"
          icon={<MessageCircle className="h-3.5 w-3.5" />}
          disabledReason={disabledReason}
        />
        <FlowButton
          reservationId={reservationId}
          messageKind="GUEST_FORM"
          label="Registre"
          ariaLabel="Enviar el formulari de registre de viatgers per WhatsApp"
          icon={<ClipboardList className="h-3.5 w-3.5" />}
          disabledReason={disabledReason}
        />
        {showAccess && (
          <FlowButton
            reservationId={reservationId}
            messageKind="ACCESS"
            label="Codi"
            ariaLabel="Enviar el codi de la porta per WhatsApp"
            icon={<KeyRound className="h-3.5 w-3.5" />}
            disabledReason={disabledReason}
          />
        )}
        <TitleWrap title={disabledReason ?? `Trucar al ${e164}`}>
          {e164 ? (
            <Button
              size="sm"
              variant="outline"
              className={cn(BUTTON_CLASS, 'border-blue-300 text-blue-700 hover:bg-blue-50')}
              asChild
            >
              <a href={`tel:${e164}`} aria-label={`Trucar al ${e164}`}>
                <Phone className="h-3.5 w-3.5" />
                Trucar
              </a>
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={BUTTON_CLASS}
              disabled
              aria-label="Trucar"
            >
              <Phone className="h-3.5 w-3.5" />
              Trucar
            </Button>
          )}
        </TitleWrap>
      </div>
      {showPhone && (
        <span
          className={cn(
            'text-[11px] min-w-0 truncate',
            phone ? 'text-gray-500' : 'text-gray-400 italic',
          )}
          title={e164 ?? undefined}
        >
          {phone || 'Sense telèfon'}
        </span>
      )}
    </div>
  );
}
